/**
 * ==========================================================================
 *  The canvas primitives every generated texture is drawn with.
 *
 *  One tile size for the whole game, a palette colour as a CSS string, and
 *  the two marks that have to survive tiling: a dot that is drawn again on
 *  the opposite edge when it straddles one, and the per-pixel grain that goes
 *  over everything at the end. `soak` is the soft radial wash the stains and
 *  the weathering are built out of.
 *
 *  Shared by `surfaces.ts`, which draws the repeating tiles, and `decals.ts`,
 *  which draws the one-off printed things.
 *
 *  Rendering only. Nothing in this file may ever be imported by `src/sim`.
 * ==========================================================================
 */

/** Every generated texture is this many pixels square. */
export const SIZE = 256;

export type Ctx = CanvasRenderingContext2D;
export type Rng = () => number;

/** A palette colour, optionally scaled and offset, as a CSS string. */
export function shade(hex: number, mul = 1, add = 0): string {
  const channel = (shift: number) =>
    Math.max(0, Math.min(255, Math.round(((hex >> shift) & 255) * mul + add)));
  return `rgb(${channel(16)},${channel(8)},${channel(0)})`;
}

/**
 * A dot that survives tiling. Anything drawn near an edge is drawn again on the
 * opposite one, so the seam between two tiles has no line down it.
 */
export function dot(ctx: Ctx, x: number, y: number, radius: number): void {
  for (const dx of [0, x < radius ? SIZE : x > SIZE - radius ? -SIZE : 0]) {
    for (const dy of [0, y < radius ? SIZE : y > SIZE - radius ? -SIZE : 0]) {
      if (dx !== 0 || dy !== 0 || (dx === 0 && dy === 0)) {
        ctx.beginPath();
        ctx.arc(x + dx, y + dy, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
}

/** Per-pixel grain over the whole tile. Everything gets a little of this. */
export function grain(ctx: Ctx, rng: Rng, strength: number): void {
  const image = ctx.getImageData(0, 0, SIZE, SIZE);
  const data = image.data;
  for (let i = 0; i < data.length; i += 4) {
    const n = (rng() - 0.5) * strength;
    data[i] = Math.max(0, Math.min(255, data[i] + n));
    data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + n));
    data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + n));
  }
  ctx.putImageData(image, 0, 0);
}

/** `shade`, with an alpha channel. Only the soft-edged recipes need it. */
export function shadeAlpha(hex: number, mul: number, alpha: number): string {
  const channel = (shift: number) => Math.min(255, Math.round(((hex >> shift) & 255) * mul));
  return `rgba(${channel(16)},${channel(8)},${channel(0)},${alpha})`;
}

/**
 * `dot`, with a soft edge: one radial gradient, tiled the same way.
 *
 * The offsets are picked rather than looped over so that a blob nowhere near an
 * edge is drawn exactly once. `dot` can afford to paint the same opaque circle
 * twice; a translucent one would come out at double strength.
 */
export function soak(ctx: Ctx, x: number, y: number, radius: number, stops: [number, string][]): void {
  const wrap = (at: number) => (at < radius ? [0, SIZE] : at > SIZE - radius ? [0, -SIZE] : [0]);
  for (const dx of wrap(x)) {
    for (const dy of wrap(y)) {
      const gradient = ctx.createRadialGradient(x + dx, y + dy, 0, x + dx, y + dy, radius);
      for (const [at, color] of stops) gradient.addColorStop(at, color);
      ctx.fillStyle = gradient;
      ctx.fillRect(x + dx - radius, y + dy - radius, radius * 2, radius * 2);
    }
  }
}
